'use client';

import dayjs from 'dayjs';
import { MonthYearTick } from './components/MonthYearTick';
import {
    BarChart as RechartsBarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import { MultiLineTickProps } from './types';
import { IMetricData } from '@/modules/configs/types';
import { NoDataView } from '../NoDataView';

// Helper function for class names
function classNames(...classes: (string | boolean | undefined)[]) {
    return classes.filter(Boolean).join(' ');
}

export function formatWeekLabel(label: string): string {
    return label.replace(/\d{4}-\d{2}-\d{2}/g, (match) => dayjs(match).format('DD-MM-YYYY'));
}


// Types for the bar chart data
export interface RegionData {
    name: string;
    value: string;
    color: string;
}

export interface BarChartData {
    // Each data point for the chart
    data: {
        [key: string]: string | number;
    }[];
    // Categories/series to display
    categories: string[];
    // Optional metadata for regions (used for summary cards)
    regions: RegionData[];
    // Optional metadata
    metadata: {
        title?: string;
        subtitle?: string;
        description?: string;
        indexKey?: string;
    };
}

// Props for the component
interface StackBarChartProps {
    data?: BarChartData;
    className?: string;
    valueFormatter?: (value: number) => string;
    stack?: boolean;
    showLegend?: boolean;
    showLabels?: boolean;
    showSummaryCards?: boolean;
    indexKey?: string;
    yAxisWidth?: number;
    responsive?: boolean;
    colors?: string[];
    segment?: string;
    metrics?: IMetricData[];
    metadata?: {
        chart?: {
            hideLegends?: boolean;
        };
    };
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: ReadonlyArray<{
        name?: string | number;
        value?: unknown;
        color?: string;
        dataKey?: string;
        payload?: any;
    }>;
    label?: string;
    valueFormatter?: (value: number) => string;
    total?: number;
    percentageKeys?: Set<string>;
}

const CustomTooltip = ({ active, payload, valueFormatter, percentageKeys }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
        const entry = payload[0];
        const raw = entry?.value;
        const numValue = typeof raw === "number" ? raw : Number(raw);
        const hasNum = Number.isFinite(numValue);
        const isPercent = !!percentageKeys && !!entry?.dataKey && percentageKeys.has(entry.dataKey);

        const formattedValue = hasNum
            ? isPercent
                ? `${numValue.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
                : (valueFormatter ? valueFormatter(numValue) : numValue)
            : (raw == null ? "" : String(raw));

        return (
            <div className="bg-white px-3 py-2 border border-gray-200 shadow-lg rounded-md">
                <div className="flex items-center gap-2 mb-1">
                    <div
                        className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: entry?.color ?? "#5a717a" }}
                    />
                    <span className="font-medium text-gray-900 text-sm">{entry?.name ?? ""}</span>
                </div>
                <div className="font-semibold text-sm text-gray-900">{formattedValue}</div>
            </div>
        );
    }
    return null;
};

// Default value formatter for currency
function defaultValueFormatter(number: number) {
    const formatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
        notation: 'compact',
        compactDisplay: 'short',
        style: 'currency',
        currency: 'USD',
    });

    return formatter.format(number);
}

// Compact number formatter for mobile (no currency symbol, just K/M/B)
function compactNumberFormatter(number: number): string {
    if (number >= 1000000000) {
        return (number / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (number >= 1000000) {
        return (number / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (number >= 1000) {
        return (number / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return number.toString();
}

export const MultiLineTick = ({ x, y, payload, maxCharsPerLine, maxLines, fontSize, lineHeight }: MultiLineTickProps) => {
    const value = payload.value;
    const valueStr = value !== null && value !== undefined ? String(value) : '';

    // Split text into lines based on character limit
    // Split on spaces, underscores, slashes, and hyphens to handle various formats
    const words = valueStr.split(/(\s+|_|\/|-)/);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
        if (!word) continue;

        const testLine = currentLine + word;

        if (testLine.length <= maxCharsPerLine) {
            currentLine = testLine;
        } else {
            if (currentLine) {
                lines.push(currentLine);
                currentLine = word;
            } else {
                // Word itself is longer than max chars, force break
                lines.push(word.substring(0, maxCharsPerLine));
                currentLine = '';
            }
        }

        if (lines.length >= maxLines) break;
    }

    if (currentLine && lines.length < maxLines) {
        lines.push(currentLine);
    }

    // Truncate if still too long
    const displayLines = lines.slice(0, maxLines).map((line, i) =>
        i === maxLines - 1 && valueStr.length > maxCharsPerLine * maxLines
            ? line.substring(0, maxCharsPerLine - 3) + '...'
            : line.substring(0, maxCharsPerLine)
    );

    return (
        <g transform={`translate(${x},${y})`}>
            <title>{valueStr}</title>
            {displayLines.map((line, i) => (
                <text
                    key={i}
                    x={0}
                    y={0}
                    dy={16 + (i * lineHeight)}
                    textAnchor="middle"
                    fill="#5a717a"
                    fontSize={fontSize}
                    fontFamily="Roboto, sans-serif"
                >
                    {line}
                </text>
            ))}
        </g>
    );
};

/**
 * Angled tick component for mobile - rotates labels to prevent overlap
 */
const AngledTick = ({ x, y, payload, fontSize }: { x: string | number; y: string | number; payload: { value: any }; fontSize: number }) => {
    const value = payload.value;
    const valueStr = value !== null && value !== undefined ? String(value) : '';

    // Truncate long text
    const displayText = valueStr.length > 15 ? valueStr.substring(0, 15) + '...' : valueStr;

    return (
        <g transform={`translate(${x},${y})`}>
            <title>{valueStr}</title>
            <text
                x={0}
                y={0}
                dy={8}
                textAnchor="end"
                fill="#212121"
                fontSize={fontSize}
                fontFamily="Roboto, sans-serif"
                transform="rotate(-45)"
            >
                {displayText}
            </text>
        </g>
    );
};

/**
 * A unified bar chart component that can be used for various bar chart visualizations
 * 
 * @param props Component properties
 * @returns Bar chart component
 */
export function StackBarChart({
    data,
    className,
    valueFormatter = defaultValueFormatter,
    stack = true,
    showLegend = true,
    showLabels = false,
    showSummaryCards = true,
    indexKey = 'date',
    yAxisWidth = 0,
    responsive = true,
    colors: customColors,
    segment,
    metrics,
    metadata
}: StackBarChartProps) {

    // Error handling - check if data structure is valid for rendering
    const isValidData = () => {
        try {
            // Check if data exists
            if (!data) return true; // Will use mock data

            // Handle standard chart data structure
            if (data.data && Array.isArray(data.data) && data.data.length > 0) {
                return true;
            }

            // Handle paid search widget response structure
            // Use type assertion for complex nested structure
            const widgetData = data as any;
            if (widgetData.widget && widgetData.widget.chartType === 'bar' &&
                widgetData.data && widgetData.data.elementCollection &&
                Array.isArray(widgetData.data.elementCollection) &&
                widgetData.data.elementCollection.length > 0) {

                // Check if we can extract metrics
                if (!widgetData.widget.metrics || !Array.isArray(widgetData.widget.metrics) || widgetData.widget.metrics.length === 0) {
                    console.error('Invalid paid search data: missing metrics array');
                    return false;
                }

                return true;
            }

            console.error('Invalid chart data structure');
            return false;
        } catch (error) {
            console.error('Error validating chart data:', error);
            return false;
        }
    };

    // If data is invalid, show error message
    if (!isValidData()) {
        return (
            <div className="bg-white rounded-lg p-6 border border-gray-200 flex flex-col items-center justify-center h-64 shadow-sm">
                <div className="bg-red-50 p-2 rounded-full mb-4">
                    <svg className="h-10 w-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-gray-800 font-medium text-lg">Chart Data Error</h3>
                <p className="text-gray-600 text-center mt-2 max-w-xs">
                    This widget contains data in a format that cannot be displayed as a bar chart.
                </p>
                <div className="mt-4 pt-4 border-t border-gray-100 w-full text-center">
                    <p className="text-gray-500 text-xs">Widget ID: {data && typeof data === 'object' && 'widget' in data && typeof data.widget === 'object' && data.widget !== null && 'prompt' in data.widget ? (data.widget.prompt as string).substring(0, 15) + '...' : 'Unknown'}</p>
                </div>
            </div>
        );
    }

    // Use provided data or fallback to mock data
    const chartData = data!;

    // Use indexKey from metadata if available, otherwise use prop default
    const actualIndexKey = chartData.metadata?.indexKey || indexKey;

    console.log("CHART DATA", chartData.categories)
    // Extract categories from data if not provided
    let categories: string[] = [];
    if (chartData.categories) {
        categories = chartData.categories;
    } else if (chartData.regions) {
        categories = chartData.regions.map(region => region.name);
    } else if (chartData.data && chartData.data.length > 0) {
        // Extract categories from the first data point (excluding the index key)
        categories = Object.keys(chartData.data[0]).filter(key => key !== actualIndexKey);
    }

    // Default colors from SimpleBarChart
    const defaultColors = ['#341cac', '#429795', '#84c4c4', '#6b46c1']; // Purple, Dark Green, Light Green, Light Purple

    // Use custom colors if provided, otherwise use defaults
    const colorsToUse = customColors && customColors.length > 0 ? customColors : defaultColors;

    // Map colors based on categories, prioritizing region colors if available
    const colors = categories.map((_, index) => colorsToUse[index % colorsToUse.length]);

    // 1. regions array (parallel to categories) — same source the right-side cards used
    const regionNameMap: Record<string, string> = {};
    if (chartData.regions) {
        categories.forEach((cat, i) => {
            if (chartData.regions[i]?.name) regionNameMap[cat] = chartData.regions[i].name;
        });
    }

    // 2. metrics prop (IMetricData array) — metricName → showName
    const displayNameMap: Record<string, string> = {};
    if (metrics && metrics.length > 0) {
        for (const m of metrics) {
            if (m.metricName) displayNameMap[m.metricName] = m.showName || m.metricName;
        }
    }

    // Resolve display name: regions first, then metrics, then camelCase → Title Case fallback
    const getDisplayName = (key: string): string =>
        regionNameMap[key] || displayNameMap[key] ||
        key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();

    // Set of metric keys whose type is 'percent' — used by tooltip to format as X%
    const percentageKeys = new Set<string>(
        (metrics || []).filter(m => m.metricType === 'percent').map(m => m.metricName)
    );

    const allPercent =
        (categories.length > 0 && categories.every(c => percentageKeys.has(c))) ||
        (chartData.regions?.length > 0 && chartData.regions.every((r: any) =>
            typeof r.value === 'string' && r.value.trim().endsWith('%')
        ));
    const yAxisTickFormatter = allPercent
        ? (value: number) => `${value}%`
        : valueFormatter;

    // Filter out data points where all categories are 0
    const filteredData = chartData.data.filter((dataPoint: any) => {
        // Check if at least one category has a non-zero value
        return categories.some(category => {
            const value = Number(dataPoint[category]) || 0;
            return value !== 0;
        });
    });

    if (filteredData.length === 0) {
        return <NoDataView />;
    }

    // Helper function to determine if a bar should have rounded corners
    const getBarRadius = (categoryIndex: number): [number, number, number, number] => {
        if (!stack) {
            return [4, 4, 0, 0]; // Non-stacked bars always have rounded tops
        }

        // For stacked bars, find the topmost visible category
        const hasVisibleData = chartData.data.some((dataPoint: any) => {
            const value = dataPoint[categories[categoryIndex]];
            return value && value > 0;
        });

        if (!hasVisibleData) {
            return [0, 0, 0, 0]; // Hidden bars get no radius
        }

        // Check if this is the topmost visible category
        const isTopmost = categories.slice(categoryIndex + 1).every((laterCategory) => {
            return !chartData.data.some((dataPoint: any) => {
                const value = dataPoint[laterCategory];
                return value && value > 0;
            });
        });

        return isTopmost ? [4, 4, 0, 0] : [0, 0, 0, 0];
    };

    return (
        <div className={classNames("w-full overflow-hidden", className || '')}>
            {/* Custom top legend — matches stacked-bar-line style */}
            {showLegend && !metadata?.chart?.hideLegends && categories.length > 0 && (
                <ul className="flex justify-start gap-5 text-primary-blue list-none p-0 mb-6 ml-8">
                    {categories.map((category, i) => (
                        <li key={category} className="flex gap-2 items-center font-normal">
                            <span className="w-3 h-3 rounded-[3px] inline-block flex-shrink-0" style={{ backgroundColor: colors[i % colors.length] }} />
                            <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>
                                {getDisplayName(category)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {/* Main content with chart and summary cards side by side */}
            <div className="flex flex-col lg:flex-row overflow-hidden">
                {/* Chart section - takes full width on mobile, adjusts for cards on desktop */}
                <div className={`${showSummaryCards && chartData.regions ? 'lg:flex-1 lg:pr-6 overflow-hidden' : 'w-full overflow-hidden'}`}>
                    {/* Desktop chart */}
                    <div className="hidden h-96 sm:block">
                        <ResponsiveContainer width="100%" height="100%">
                            <RechartsBarChart
                                data={filteredData}
                                margin={{
                                    top: 20,
                                    right: 70,
                                    left: yAxisWidth || 60,
                                    bottom: 30,
                                }}
                                barSize={categories.length === 1 ? 60 : undefined}
                                maxBarSize={60}
                                barCategoryGap={categories.length === 1 ? "60%" : "20%"}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={true}
                                    horizontal={true}
                                    stroke="#e5e7eb"
                                />
                                <XAxis
                                    dataKey={actualIndexKey}
                                    tick={(props) => {
                                        if (segment === 'month') {
                                            return (
                                                <MonthYearTick
                                                    {...props}
                                                    fontSize={12}
                                                    lineHeight={14}
                                                />
                                            );
                                        }
                                        const isWeek = segment === 'week';
                                        const tickProps = isWeek
                                            ? { ...props, payload: { ...props.payload, value: formatWeekLabel(props.payload.value) } }
                                            : props;
                                        return (
                                            <MultiLineTick
                                                {...tickProps}
                                                maxCharsPerLine={isWeek ? 30 : 12}
                                                maxLines={2}
                                                fontSize={12}
                                                lineHeight={14}
                                            />
                                        );
                                    }}
                                    interval={0} /* Show all labels */
                                    tickMargin={10}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                />

                                <YAxis
                                    tick={{ fontSize: 12, fill: '#212121' }}
                                    tickFormatter={yAxisTickFormatter}
                                    tickMargin={10}
                                    width={yAxisWidth || 50}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                />

                                <Tooltip
                                    cursor={false}
                                    shared={false}
                                    content={(props) => (
                                        <CustomTooltip
                                            active={props.active}
                                            payload={props.payload as any}
                                            label={props.label ? String(props.label) : undefined}
                                            valueFormatter={valueFormatter}
                                            percentageKeys={percentageKeys}
                                        />
                                    )}
                                />

                                {categories.map((category, idx) => (
                                    <Bar
                                        key={category}
                                        dataKey={category}
                                        name={getDisplayName(category)}
                                        fill={colors[idx % colors.length]}
                                        stackId={stack ? "stack" : undefined}
                                        isAnimationActive={true}
                                        animationDuration={1000}
                                        animationEasing="ease-out"
                                        radius={getBarRadius(idx)}
                                    >
                                        {showLabels && (
                                            <LabelList
                                                dataKey={category}
                                                position="top"
                                                formatter={(value: any) => {
                                                    if (typeof value === 'number') {
                                                        return valueFormatter(value);
                                                    }
                                                    return value;
                                                }}
                                                style={{ fontSize: 12, fill: '#212121', fontWeight: 500 }}
                                            />
                                        )}
                                    </Bar>
                                ))}
                            </RechartsBarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Mobile chart */}
                    {responsive && (
                        <div className="h-64 sm:hidden">
                            <ResponsiveContainer width="100%" height="100%">
                                <RechartsBarChart
                                    data={filteredData}
                                    margin={{
                                        top: 20,
                                        right: 50,
                                        left: yAxisWidth || 45,
                                        bottom: 40,
                                    }}
                                    barSize={categories.length === 1 ? 30 : undefined}
                                    maxBarSize={60}
                                    barCategoryGap={categories.length === 1 ? "60%" : "20%"}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        vertical={true}
                                        horizontal={true}
                                        stroke="#e5e7eb"
                                    />

                                    <XAxis
                                        dataKey={actualIndexKey}
                                        tick={(props) => {
                                            if (segment === 'month') {
                                                return (
                                                    <MonthYearTick
                                                        {...props}
                                                        fontSize={9}
                                                        lineHeight={12}
                                                    />
                                                );
                                            }
                                            return (
                                                <AngledTick
                                                    {...props}
                                                    fontSize={9}
                                                />
                                            );
                                        }}
                                        interval={0}
                                        tickMargin={10}
                                        axisLine={{ stroke: '#e0e0e0' }}
                                        height={60}
                                    />

                                    <YAxis
                                        tick={{ fontSize: 10, fill: '#212121' }}
                                        tickFormatter={allPercent ? (v: number) => `${v}%` : compactNumberFormatter}
                                        tickMargin={5}
                                        width={40}
                                        axisLine={{ stroke: '#e0e0e0' }}
                                    />

                                    <Tooltip
                                        cursor={false}
                                        shared={false}
                                        content={(props) => (
                                            <CustomTooltip
                                                active={props.active}
                                                payload={props.payload as any}
                                                label={props.label ? String(props.label) : undefined}
                                                valueFormatter={valueFormatter}
                                            />
                                        )}
                                    />

                                    {categories.map((category, idx) => (
                                        <Bar
                                            key={category}
                                            dataKey={category}
                                            name={getDisplayName(category)}
                                            fill={colors[idx % colors.length]}
                                            stackId={stack ? "stack" : undefined}
                                            isAnimationActive={true}
                                            animationDuration={1000}
                                            animationEasing="ease-out"
                                            radius={getBarRadius(idx)}
                                        />
                                    ))}
                                </RechartsBarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                {/* Summary Cards - on the right side on desktop, below chart on mobile */}
                {showSummaryCards && chartData.regions && (
                    <div className="mt-6 lg:mt-0 lg:w-[180px] lg:flex-shrink-0 overflow-hidden">
                        <ul
                            role="list"
                            className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 max-h-96 overflow-y-auto"
                        >
                            {chartData.regions.map((region, index) => {
                                // Get the color for this region (use same colors as the bars)
                                const color = colors[index % colors.length];

                                return (
                                    <li
                                        key={region.name}
                                        className="rounded-tremor-small border border-gray-300 rounded px-3 py-2 text-left dark:border-dark-tremor-border w-full max-w-11/12"
                                        style={{ height: '58px' }}
                                    >
                                        <div className="flex items-center space-x-1.5">
                                            {/* Only show color indicators when there are multiple categories */}
                                            {categories.length > 1 && (
                                                <span
                                                    className="size-2.5 rounded-sm"
                                                    style={{ backgroundColor: color }}
                                                    aria-hidden={true}
                                                />
                                            )}
                                            <p
                                                className="truncate"
                                                style={{
                                                    color: '#2f363f',
                                                    fontSize: '12px',
                                                    fontFamily: 'Roboto, sans-serif',
                                                    maxWidth: categories.length > 1 ? '100px' : '120px', // Give more space when no color indicator
                                                }}
                                                title={region.name} // Show full name on hover
                                            >
                                                {region.name}
                                            </p>
                                        </div>
                                        <p
                                            className="mt-0.5 font-semibold"
                                            style={{
                                                color: '#2f363f',
                                                fontSize: '12px',
                                                fontFamily: 'Roboto, sans-serif'
                                            }}
                                        >
                                            {region.value}
                                        </p>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
